/**
 * EventosAdminPage — panel admin de la ticketera de pagafacil.eu.
 *
 * Ruta: /operador/eventos?t=<token>
 *
 * Requiere el mismo magic-link (t=) que /operador. Ofrece 3 vistas en
 * tabs internos:
 *   1) Lista de shows (ShowsList)
 *   2) Editor (crear / editar un show)
 *   3) Pedidos de un show concreto
 *
 * Backend: /api/admin/shows (protegido con dep _require_operator_token).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';
import TopBar from '../components/TopBar';
import BrandColorPicker from '../components/BrandColorPicker';
import StickyFormLayout, { type StickyFormSection } from '../components/StickyFormLayout';
import LocationMap from '../components/LocationMap';

type Status = 'draft' | 'on_sale' | 'sold_out' | 'cancelled' | 'completed';

interface Show {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  poster_url?: string | null;
  artist_name?: string | null;
  event_datetime: string;
  doors_open_at?: string | null;
  venue_name?: string | null;
  venue_address?: string | null;
  venue_city?: string | null;
  venue_maps_url?: string | null;
  ticket_price?: number | null;
  ticket_price_min?: number | null;
  ticket_price_max?: number | null;
  capacity?: number | null;
  tickets_sold: number;
  tickets_available?: number | null;
  status: Status;
  is_public: boolean;
  online_sales_closes_at?: string | null;
  promote_in_emails: boolean;
  organizer_note?: string | null;
  entity_id?: string | null;
  entity_name?: string | null;
  entity_slug?: string | null;
}

interface EntityLite {
  id: string;
  slug: string;
  name: string;
}

interface OrderRow {
  id: string;
  order_reference: string;
  buyer_name: string;
  buyer_email: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  status: string;
  tickets_validated: number;
  created_at: string;
}

type View =
  | { kind: 'list' }
  | { kind: 'edit'; show: Show | null }
  | { kind: 'orders'; show: Show }
  | { kind: 'entities' }
  | { kind: 'entity-edit'; entity: EntityFull | null };

interface EntityFull {
  id: string;
  slug: string;
  name: string;
  entity_type?: string | null;
  description?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  city?: string | null;
  province?: string | null;
  logo_url?: string | null;
  cover_url?: string | null;
  accent_color?: string | null;
  website_url?: string | null;
  instagram_url?: string | null;
  public_url?: string;
}

export default function EventosAdminPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  if (!token) {
    return (
      <div className="min-h-screen bg-surface text-ink flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="font-display font-bold text-3xl mb-3">Sin token de operador</h1>
          <p className="text-ink-500 text-[14px]">
            Pide un magic link desde <Link to="/operador" className="underline">/operador</Link> y usa el enlace del email.
          </p>
        </div>
      </div>
    );
  }

  return <AdminApp token={token} />;
}

function AdminApp({ token }: { token: string }) {
  const [shows, setShows] = useState<Show[]>([]);
  const [entities, setEntities] = useState<EntityFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>({ kind: 'list' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Show[]>(`/admin/shows?t=${encodeURIComponent(token)}`);
      setShows(r.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al cargar shows');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadEntities = useCallback(async () => {
    try {
      // Listado light + detalle completo para cada una (para poblar branding)
      const light = await api.get<EntityLite[]>(`/operator/entities?t=${encodeURIComponent(token)}`);
      const details = await Promise.all(
        (light.data || []).map((e) =>
          api.get<EntityFull>(`/operator/entities/${e.id}?t=${encodeURIComponent(token)}`)
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      setEntities(details.filter((x): x is EntityFull => x !== null));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al cargar organizadores');
    }
  }, [token]);

  useEffect(() => {
    document.title = 'Eventos · Operador';
    void load();
    void loadEntities();
  }, [load, loadEntities]);

  const inEntitiesArea = view.kind === 'entities' || view.kind === 'entity-edit';

  return (
    <div className="min-h-screen bg-surface text-ink font-sans">
      <TopBar contextLabel="Operador · Eventos" />
      <header className="bg-paper border-b border-line">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-[28px]">
              {inEntitiesArea ? 'Organizadores' : 'Eventos propios'}
            </h1>
            <p className="text-[13.5px] text-ink-500 mt-1">
              {inEntitiesArea
                ? 'Entidades con landing propia en /o/<slug>'
                : 'Ticketera pagafacil.eu · shows organizados por Pagafácil S.L.'}
            </p>
          </div>
          <div className="flex gap-2">
            {view.kind !== 'list' && view.kind !== 'entities' && (
              <button onClick={() => setView(inEntitiesArea ? { kind: 'entities' } : { kind: 'list' })} className="px-4 py-2 border border-line rounded-md text-[13.5px]">
                ← Volver
              </button>
            )}
            {view.kind === 'list' && (
              <button
                onClick={() => setView({ kind: 'edit', show: null })}
                className="px-4 py-2 bg-brand text-white rounded-md text-[13.5px] font-semibold"
              >
                + Nuevo evento
              </button>
            )}
            {view.kind === 'entities' && (
              <button
                onClick={() => setView({ kind: 'entity-edit', entity: null })}
                className="px-4 py-2 bg-brand text-white rounded-md text-[13.5px] font-semibold"
              >
                + Nuevo organizador
              </button>
            )}
          </div>
        </div>
        {/* Tabs simples */}
        <div className="max-w-6xl mx-auto px-6 flex gap-1 border-t border-line">
          <button
            onClick={() => setView({ kind: 'list' })}
            className={`px-4 py-3 text-[13px] font-semibold border-b-2 ${!inEntitiesArea ? 'border-brand text-brand' : 'border-transparent text-ink-500 hover:text-ink'}`}
          >Eventos ({shows.length})</button>
          <button
            onClick={() => setView({ kind: 'entities' })}
            className={`px-4 py-3 text-[13px] font-semibold border-b-2 ${inEntitiesArea ? 'border-brand text-brand' : 'border-transparent text-ink-500 hover:text-ink'}`}
          >Organizadores ({entities.length})</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading && <div className="opacity-60">Cargando…</div>}

        {!loading && view.kind === 'list' && (
          <ShowsList
            shows={shows}
            onEdit={(s) => setView({ kind: 'edit', show: s })}
            onOrders={(s) => setView({ kind: 'orders', show: s })}
            onAction={async (s, action) => {
              try {
                if (action === 'onsale') {
                  await api.post(`/admin/shows/${s.id}/onsale?t=${encodeURIComponent(token)}`);
                } else if (action === 'cancel') {
                  await api.post(`/admin/shows/${s.id}/cancel?t=${encodeURIComponent(token)}`);
                } else if (action === 'pause') {
                  // Vuelve a borrador (retirar de venta pero no cancelar reservas ya
                  // hechas). PATCH directo — no toca is_public, así puede seguir
                  // visible como "aviso" o el operador la puede ocultar aparte.
                  await api.patch(`/admin/shows/${s.id}?t=${encodeURIComponent(token)}`, { status: 'draft' });
                } else if (action === 'reactivate') {
                  // Cancelled → draft. Después el operador decide si vuelve a
                  // "A la venta" con el flujo normal (revisa fecha, precios, aforo).
                  await api.patch(`/admin/shows/${s.id}?t=${encodeURIComponent(token)}`, { status: 'draft' });
                }
                toast.success('Actualizado');
                await load();
              } catch (e: any) {
                toast.error(e?.response?.data?.detail || 'Error');
              }
            }}
          />
        )}

        {view.kind === 'edit' && (
          <ShowEditor
            token={token}
            existing={view.show}
            onSaved={async () => {
              await load();
              setView({ kind: 'list' });
            }}
          />
        )}

        {view.kind === 'orders' && <ShowOrders token={token} show={view.show} />}

        {view.kind === 'entities' && (
          <EntitiesList
            entities={entities}
            onEdit={(e) => setView({ kind: 'entity-edit', entity: e })}
          />
        )}

        {view.kind === 'entity-edit' && (
          <EntityEditor
            token={token}
            existing={view.entity}
            onSaved={async () => {
              await loadEntities();
              setView({ kind: 'entities' });
            }}
          />
        )}
      </main>
    </div>
  );
}

/* ── Organizadores (entidades) ─────────────────────────────────── */

function EntitiesList({ entities, onEdit }: { entities: EntityFull[]; onEdit: (e: EntityFull) => void }) {
  if (entities.length === 0) {
    return (
      <div className="bg-paper border border-line rounded-lg p-10 text-center">
        <div className="font-display font-bold text-xl mb-2">No hay organizadores todavía</div>
        <div className="text-ink-500 text-[14px]">Pulsa "+ Nuevo organizador" para crear una entidad con landing propia.</div>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {entities.map((en) => (
        <div key={en.id} className="bg-paper border border-line rounded-lg p-4 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {en.logo_url ? (
              <img src={en.logo_url} alt={en.name} className="w-14 h-14 rounded-lg object-cover border border-line" />
            ) : (
              <div
                className="w-14 h-14 rounded-lg flex items-center justify-center font-display font-bold text-lg text-white"
                style={{ background: en.accent_color || '#1E2A2E' }}
              >{en.name.charAt(0).toUpperCase()}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-lg">{en.name}</div>
              <div className="text-[13px] text-ink-500 mt-0.5">
                {en.entity_type ? `${en.entity_type} · ` : ''}
                {en.city && `${en.city}${en.province ? ' (' + en.province + ')' : ''}`}
              </div>
              <div className="text-[12px] font-mono text-ink-500 mt-1">
                <a href={`/o/${en.slug}`} target="_blank" rel="noreferrer" className="underline">/o/{en.slug}</a>
                {en.contact_email && <span className="ml-3">· {en.contact_email}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onEdit(en)} className="px-3 py-1.5 border border-line rounded text-[13px]">Editar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

interface EntityForm {
  name: string;
  contact_email: string;
  contact_name: string;
  contact_phone: string;
  entity_type: string;
  city: string;
  province: string;
  description: string;
  logo_url: string;
  cover_url: string;
  accent_color: string;
  website_url: string;
  instagram_url: string;
}

const emptyEntityForm = (): EntityForm => ({
  name: '', contact_email: '', contact_name: '', contact_phone: '',
  entity_type: '', city: '', province: '', description: '',
  logo_url: '', cover_url: '', accent_color: '', website_url: '', instagram_url: '',
});

function EntityEditor({ token, existing, onSaved }: { token: string; existing: EntityFull | null; onSaved: () => void | Promise<void> }) {
  const isEdit = !!existing;
  const [form, setForm] = useState<EntityForm>(() =>
    existing ? {
      name: existing.name || '',
      contact_email: existing.contact_email || '',
      contact_name: existing.contact_name || '',
      contact_phone: existing.contact_phone || '',
      entity_type: existing.entity_type || '',
      city: existing.city || '',
      province: existing.province || '',
      description: existing.description || '',
      logo_url: existing.logo_url || '',
      cover_url: existing.cover_url || '',
      accent_color: existing.accent_color || '',
      website_url: existing.website_url || '',
      instagram_url: existing.instagram_url || '',
    } : emptyEntityForm()
  );
  const [busy, setBusy] = useState(false);
  const [colorValid, setColorValid] = useState(true);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!colorValid) {
      toast.error('El color de acento no cumple los requisitos de contraste o colisiona con estados del sistema.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_email: form.contact_email.trim(),
        contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
        entity_type: form.entity_type || null,
        city: form.city || null,
        province: form.province || null,
        description: form.description || null,
        logo_url: form.logo_url || null,
        cover_url: form.cover_url || null,
        accent_color: form.accent_color || null,
        website_url: form.website_url || null,
        instagram_url: form.instagram_url || null,
      };
      if (isEdit && existing) {
        await api.patch(`/operator/entities/${existing.id}?t=${encodeURIComponent(token)}`, payload);
      } else {
        await api.post(`/operator/entities?t=${encodeURIComponent(token)}`, payload);
      }
      toast.success(isEdit ? 'Organizador actualizado' : 'Organizador creado');
      await onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  const preview = (
    <div
      className="rounded-lg overflow-hidden border border-line"
      style={{
        background: form.cover_url
          ? `linear-gradient(180deg, rgba(30,42,46,0.35), rgba(30,42,46,0.85)), url(${form.cover_url}) center/cover`
          : `linear-gradient(135deg, ${form.accent_color || '#1E2A2E'}, #1E2A2E)`,
        color: '#FFFFFF',
        padding: 20,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 16,
        minHeight: 140,
      }}
    >
      {form.logo_url && (
        <img src={form.logo_url} alt="logo" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '2px solid #FFF', background: '#FFF' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-75">{form.entity_type || 'Organizador'}</div>
        <div className="font-display font-bold text-xl">{form.name || 'Nombre del organizador'}</div>
        {(form.city || form.province) && (
          <div className="text-[12px] opacity-85">{[form.city, form.province].filter(Boolean).join(' · ')}</div>
        )}
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-paper border border-line rounded-lg p-6 space-y-6">
      <div>
        <h2 className="font-display font-bold text-xl">{isEdit ? 'Editar organizador' : 'Nuevo organizador'}</h2>
        {existing && (
          <p className="text-[13px] text-ink-500 mt-1">
            Landing pública:{' '}
            <a href={`/o/${existing.slug}`} target="_blank" rel="noreferrer" className="underline font-mono">/o/{existing.slug}</a>
          </p>
        )}
      </div>

      <fieldset className="space-y-3">
        <legend className="font-mono uppercase text-[11px] tracking-widest text-ink-500">Identidad</legend>
        <div className="grid grid-cols-2 gap-3">
          <Text label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Text label="Tipo" value={form.entity_type} onChange={(v) => setForm({ ...form, entity_type: v })} placeholder="hermandad, ampa, club..." />
          <Text label="Ciudad" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Text label="Provincia" value={form.province} onChange={(v) => setForm({ ...form, province: v })} />
        </div>
        <TextArea label="Descripción (bio pública)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="font-mono uppercase text-[11px] tracking-widest text-ink-500">Contacto</legend>
        <div className="grid grid-cols-2 gap-3">
          <Text type="email" label="Email de contacto" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} required />
          <Text label="Nombre contacto" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
          <Text label="Teléfono" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="font-mono uppercase text-[11px] tracking-widest text-ink-500">Branding (landing pública)</legend>
        <div className="grid grid-cols-2 gap-3">
          <Text label="URL del logo" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} placeholder="https://…" />
          <Text label="URL de portada" value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} placeholder="https://…" />
          <Text label="Web" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} placeholder="https://tu-web.com" />
          <Text label="Instagram" value={form.instagram_url} onChange={(v) => setForm({ ...form, instagram_url: v })} placeholder="https://instagram.com/…" />
        </div>
        <div className="mt-2">
          <BrandColorPicker
            value={form.accent_color}
            onChange={(v) => setForm({ ...form, accent_color: v })}
            onValidationChange={setColorValid}
          />
        </div>
        <div className="mt-2">
          <div className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500 mb-1">Vista previa del hero</div>
          {preview}
        </div>
      </fieldset>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || !colorValid}
          className="px-6 py-2.5 bg-brand text-white rounded-md font-semibold disabled:opacity-50"
          title={!colorValid ? 'Corrige el color de acento antes de guardar' : ''}
        >
          {busy ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear organizador'}
        </button>
      </div>
    </form>
  );
}

/* ── Lista ─────────────────────────────────────────────────────── */

function ShowsList({
  shows,
  onEdit,
  onOrders,
  onAction,
}: {
  shows: Show[];
  onEdit: (s: Show) => void;
  onOrders: (s: Show) => void;
  onAction: (s: Show, action: 'onsale' | 'cancel' | 'pause' | 'reactivate') => void | Promise<void>;
}) {
  if (shows.length === 0) {
    return (
      <div className="bg-paper border border-line rounded-lg p-10 text-center">
        <div className="font-display font-bold text-xl mb-2">Todavía no has creado ningún evento</div>
        <div className="text-ink-500 text-[14px]">Pulsa "+ Nuevo evento" para empezar.</div>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {shows.map((s) => (
        <div key={s.id} className="bg-paper border border-line rounded-lg p-4 flex flex-wrap gap-4 items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-lg">{s.title}</div>
              <StatusChip status={s.status} />
              {s.promote_in_emails && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink px-2 py-0.5 rounded" style={{ background: '#F4E3A6' }}>EMAIL</span>
              )}
              {s.entity_slug && (
                <span
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-brand-50 text-brand-700"
                  title={`Landing: /o/${s.entity_slug}`}
                >
                  · {s.entity_name || s.entity_slug}
                </span>
              )}
            </div>
            <div className="text-[13px] text-ink-500 mt-1">
              {new Date(s.event_datetime).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
              {s.venue_city ? ` · ${s.venue_city}` : ''} · <span className="font-mono">/e/{s.slug}</span>
            </div>
            <div className="text-[12px] text-ink-500 mt-1">
              Vendidas: <b>{s.tickets_sold}</b>
              {s.capacity != null && ` / ${s.capacity}`}
              {s.ticket_price != null && ` · ${Number(s.ticket_price).toFixed(2)} €`}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => onOrders(s)} className="px-3 py-1.5 border border-line rounded text-[13px]">Pedidos</button>
            <button onClick={() => onEdit(s)} className="px-3 py-1.5 border border-line rounded text-[13px]">Editar</button>
            {(s.status === 'draft' || s.status === 'sold_out') && (
              <button onClick={() => onAction(s, 'onsale')} className="px-3 py-1.5 bg-brand text-white rounded text-[13px]">A la venta</button>
            )}
            {s.status === 'on_sale' && (
              <button
                onClick={() => {
                  if (confirm(`Pausar la venta de "${s.title}"? Vuelve a borrador. Las reservas ya confirmadas se mantienen; solo se para la venta nueva.`)) {
                    onAction(s, 'pause');
                  }
                }}
                className="px-3 py-1.5 border border-amber-300 text-amber-700 rounded text-[13px]"
              >Pausar venta</button>
            )}
            {s.status === 'cancelled' && (
              <button
                onClick={() => onAction(s, 'reactivate')}
                className="px-3 py-1.5 border border-line rounded text-[13px] font-semibold"
                title="Reactiva el evento en estado borrador. Después edítalo y ponlo A la venta cuando quieras."
              >Reactivar</button>
            )}
            {s.status !== 'cancelled' && s.status !== 'completed' && (
              <button onClick={() => onAction(s, 'cancel')} className="px-3 py-1.5 border border-red-300 text-red-700 rounded text-[13px]">Cancelar</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusChip({ status }: { status: Status }) {
  const map: Record<Status, { label: string; cls: string }> = {
    draft: { label: 'Borrador', cls: 'bg-line text-ink-500' },
    on_sale: { label: 'A la venta', cls: 'bg-olive-50 text-olive-700' },
    sold_out: { label: 'Agotado', cls: 'bg-ink text-white' },
    cancelled: { label: 'Cancelado', cls: 'bg-red-100 text-red-700' },
    completed: { label: 'Cerrado', cls: 'bg-line text-ink-500' },
  };
  const m = map[status] || map.draft;
  return <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

/* ── Editor ────────────────────────────────────────────────────── */

interface EditorForm {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  poster_url: string;
  artist_name: string;
  event_datetime: string;
  doors_open_at: string;
  venue_name: string;
  venue_address: string;
  venue_city: string;
  venue_maps_url: string;
  ticket_price: string;
  ticket_price_min: string;
  ticket_price_max: string;
  capacity: string;
  online_sales_closes_at: string;
  is_public: boolean;
  promote_in_emails: boolean;
  organizer_note: string;
  entity_id: string;  // '' = ninguna (queda como show del operador principal)
  // Campos ricos (paridad con kpop)
  category: string;
  event_type: string;
  included: string;
  terms: string;
  pickup_points: string;
  nominative_tickets: boolean;
}

function emptyForm(): EditorForm {
  return {
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    poster_url: '',
    artist_name: '',
    event_datetime: '',
    doors_open_at: '',
    venue_name: '',
    venue_address: '',
    venue_city: '',
    venue_maps_url: '',
    ticket_price: '',
    ticket_price_min: '',
    ticket_price_max: '',
    capacity: '',
    online_sales_closes_at: '',
    is_public: false,
    promote_in_emails: false,
    organizer_note: '',
    entity_id: '',
    category: '',
    event_type: '',
    included: '',
    terms: '',
    pickup_points: '',
    nominative_tickets: false,
  };
}

function toDatetimeLocal(iso?: string | null): string {
  if (!iso) return '';
  // OJO: NO usar toISOString() aquí — devuelve UTC. Un input
  // <datetime-local> espera hora LOCAL. Antes hacíamos
  // new Date(iso).toISOString().slice(0,16) y el hueco horario
  // (Madrid = UTC+1/+2) hacía que el editor mostrase "14:00" para un
  // evento guardado a las "16:00", el usuario cambiaba y al releer
  // aparecía otra hora distorsionada. Reportado por operador jul-2026.
  //
  // Solución: construir el string local con getters locales.
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ShowEditor({ token, existing, onSaved }: { token: string; existing: Show | null; onSaved: () => void | Promise<void> }) {
  const isEdit = !!existing;
  const [form, setForm] = useState<EditorForm>(() =>
    existing
      ? {
          slug: existing.slug,
          title: existing.title,
          subtitle: existing.subtitle || '',
          description: existing.description || '',
          poster_url: existing.poster_url || '',
          artist_name: existing.artist_name || '',
          event_datetime: toDatetimeLocal(existing.event_datetime),
          doors_open_at: toDatetimeLocal(existing.doors_open_at),
          venue_name: existing.venue_name || '',
          venue_address: existing.venue_address || '',
          venue_city: existing.venue_city || '',
          venue_maps_url: existing.venue_maps_url || '',
          ticket_price: existing.ticket_price != null ? String(existing.ticket_price) : '',
          ticket_price_min: existing.ticket_price_min != null ? String(existing.ticket_price_min) : '',
          ticket_price_max: existing.ticket_price_max != null ? String(existing.ticket_price_max) : '',
          capacity: existing.capacity != null ? String(existing.capacity) : '',
          online_sales_closes_at: toDatetimeLocal(existing.online_sales_closes_at),
          is_public: existing.is_public,
          promote_in_emails: existing.promote_in_emails,
          organizer_note: existing.organizer_note || '',
          entity_id: existing.entity_id || '',
          category: (existing as any).category || '',
          event_type: (existing as any).event_type || '',
          included: (existing as any).included || '',
          terms: (existing as any).terms || '',
          pickup_points: (existing as any).pickup_points || '',
          nominative_tickets: !!(existing as any).nominative_tickets,
        }
      : emptyForm()
  );
  const [busy, setBusy] = useState(false);
  const [entities, setEntities] = useState<EntityLite[]>([]);

  useEffect(() => {
    api.get<EntityLite[]>(`/operator/entities?t=${encodeURIComponent(token)}`)
      .then((r) => setEntities(r.data || []))
      .catch(() => { /* silent — dropdown queda vacío, se puede seguir sin entity */ });
  }, [token]);

  const numberOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        slug: form.slug.trim().toLowerCase(),
        title: form.title.trim(),
        subtitle: form.subtitle || null,
        description: form.description || null,
        poster_url: form.poster_url || null,
        artist_name: form.artist_name || null,
        event_datetime: form.event_datetime,
        doors_open_at: form.doors_open_at || null,
        venue_name: form.venue_name || null,
        venue_address: form.venue_address || null,
        venue_city: form.venue_city || null,
        venue_maps_url: form.venue_maps_url || null,
        ticket_price: numberOrNull(form.ticket_price),
        ticket_price_min: numberOrNull(form.ticket_price_min),
        ticket_price_max: numberOrNull(form.ticket_price_max),
        capacity: numberOrNull(form.capacity),
        online_sales_closes_at: form.online_sales_closes_at || null,
        is_public: form.is_public,
        promote_in_emails: form.promote_in_emails,
        organizer_note: form.organizer_note || null,
        entity_id: form.entity_id || null,
        category: form.category || null,
        event_type: form.event_type || null,
        included: form.included || null,
        terms: form.terms || null,
        pickup_points: form.pickup_points || null,
        nominative_tickets: form.nominative_tickets,
      };
      if (isEdit && existing) {
        // PATCH no incluye slug
        const { slug, ...update } = payload;
        void slug;
        await api.patch(`/admin/shows/${existing.id}?t=${encodeURIComponent(token)}`, update);
      } else {
        await api.post(`/admin/shows?t=${encodeURIComponent(token)}`, payload);
      }
      toast.success(isEdit ? 'Actualizado' : 'Creado');
      await onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  const sections: StickyFormSection[] = [
    {
      id: 'identidad', label: 'Identidad', icon: '🎭',
      content: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Text label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required disabled={isEdit} placeholder="mi-evento-2026" />
            <Text label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <Text label="Subtítulo" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} help="Frase corta bajo el título. Ej: “Un espectáculo de magia para toda la familia”." />
            <Text label="Artista" value={form.artist_name} onChange={(v) => setForm({ ...form, artist_name: v })} />
            <Text label="URL del cartel" value={form.poster_url} onChange={(v) => setForm({ ...form, poster_url: v })} className="col-span-2" />
          </div>
          <TextArea
            label="Descripción larga del evento"
            value={form.description}
            onChange={(v) => setForm({ ...form, description: v })}
            rows={5}
            placeholder="Cuenta con detalle qué pasa en el evento: qué incluye la entrada, horarios, público objetivo, precios especiales…&#10;&#10;Ejemplo bus: «Autobús ida y vuelta al Aquópolis desde El Cuervo. Salida a las 10:00 desde la Plaza Mayor, regreso a las 20:00. Precio incluye ida, vuelta y entrada al parque.»"
            help="Aparece en la landing pública /e/<slug> bajo el hero, en tipografía grande. Puedes usar saltos de línea normales — se respetan tal cual."
          />
        </div>
      ),
    },
    {
      id: 'cuando-donde', label: 'Cuándo y dónde', icon: '📍',
      content: (
        <div className="grid grid-cols-2 gap-3">
          <Text type="datetime-local" label="Fecha y hora" value={form.event_datetime} onChange={(v) => setForm({ ...form, event_datetime: v })} required />
          <Text type="datetime-local" label="Puertas (opcional)" value={form.doors_open_at} onChange={(v) => setForm({ ...form, doors_open_at: v })} />
          <Text label="Local" value={form.venue_name} onChange={(v) => setForm({ ...form, venue_name: v })} />
          <Text label="Ciudad" value={form.venue_city} onChange={(v) => setForm({ ...form, venue_city: v })} />
          <Text label="Dirección" value={form.venue_address} onChange={(v) => setForm({ ...form, venue_address: v })} className="col-span-2" />
          <Text label="URL de Google Maps" value={form.venue_maps_url} onChange={(v) => setForm({ ...form, venue_maps_url: v })} className="col-span-2" />
          <div className="col-span-2">
            <LocationMap
              address={form.venue_address}
              city={form.venue_city}
              externalUrl={form.venue_maps_url}
              height={200}
              label="Vista previa del mapa"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'entradas', label: 'Entradas', icon: '🎟',
      content: (
        <div className="space-y-4">
          <div>
            <div className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500 mb-2">Precios y aforo</div>
            <div className="grid grid-cols-4 gap-3">
              <Text type="number" step="0.01" label="Precio" value={form.ticket_price} onChange={(v) => setForm({ ...form, ticket_price: v })} />
              <Text type="number" step="0.01" label="Precio mín" value={form.ticket_price_min} onChange={(v) => setForm({ ...form, ticket_price_min: v })} />
              <Text type="number" step="0.01" label="Precio máx" value={form.ticket_price_max} onChange={(v) => setForm({ ...form, ticket_price_max: v })} />
              <Text type="number" label="Aforo" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} />
            </div>
          </div>

          {/* Bloque destacado: cierre de venta online.
              Reportado por Ayto. El Cuervo (jul-2026): el campo pasaba
              desapercibido en el grid. Lo sacamos a un card con fondo cream
              y helper explícito, incluyendo un botón rápido "usa fecha del evento". */}
          <div className="rounded-md p-4 border border-line" style={{ background: '#FAF7F4' }}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="font-display font-bold text-[15px]">Cierre de la venta online</div>
                <div className="text-[12.5px] text-ink-500 mt-0.5">
                  Fecha y hora en la que dejamos de vender por internet. A partir de ese momento, la web muestra "cerrado" y solo se puede vender en taquilla.
                </div>
              </div>
              {form.event_datetime && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, online_sales_closes_at: form.event_datetime })}
                  className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 border border-line rounded bg-white hover:bg-surface"
                  title="Copia la fecha/hora del evento"
                >
                  · usar hora del evento
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              value={form.online_sales_closes_at}
              onChange={(e) => setForm({ ...form, online_sales_closes_at: e.target.value })}
              className="border border-line rounded-md px-3 py-2 text-[14px] bg-white w-full sm:w-auto"
            />
            <div className="text-[11.5px] text-ink-500 mt-2">
              <b>Vacío = venta abierta hasta el día del evento.</b>
              &nbsp;·&nbsp; Ejemplo bus: si el bus sale a las 16:00, pon <span className="font-mono">el mismo día a las 15:30</span> para que nadie compre justo antes de salir.
            </div>
          </div>

          <Check
            label="Entradas nominativas — pedir DNI, apellidos y dirección en el flujo de compra"
            checked={form.nominative_tickets}
            onChange={(v) => setForm({ ...form, nominative_tickets: v })}
          />
        </div>
      ),
    },
    {
      id: 'extras', label: 'Extras', icon: '✨',
      content: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Text label="Categoría" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="Bus a fiesta, Concierto, Teatro..." />
            <Text label="Tipo de evento" value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} placeholder="concierto, bus, teatro, festival..." />
          </div>
          <TextArea label="¿Qué incluye tu entrada?" value={form.included} onChange={(v) => setForm({ ...form, included: v })} rows={3} help="Ej: ida y vuelta en autobús · 1ª consumición gratis · fiesta hasta las 6h" />
          <TextArea label="Puntos de recogida / horarios" value={form.pickup_points} onChange={(v) => setForm({ ...form, pickup_points: v })} rows={3} help="Útil para viajes en bus. Ej: 21:30 Lebrija (Plaza España) · 22:00 Sevilla (Nervión)" />
          <TextArea label="Condiciones específicas" value={form.terms} onChange={(v) => setForm({ ...form, terms: v })} rows={3} help="Ej: entrada +18 · no se admiten cambios · el organizador se reserva el derecho de admisión" />
        </div>
      ),
    },
    {
      id: 'organizador', label: 'Organizador', icon: '🏢',
      content: (
        <label className="flex flex-col gap-1">
          <span className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500">
            Entidad dueña de este evento
          </span>
          <select
            value={form.entity_id}
            onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
            className="border border-line rounded-md px-3 py-2 text-[14px] bg-surface"
          >
            <option value="">— Operador principal (sale en cartelera global /entradas) —</option>
            {entities.map((en) => (
              <option key={en.id} value={en.id}>{en.name} · /o/{en.slug}</option>
            ))}
          </select>
          <span className="text-[11px] text-ink-500">
            Si eliges una entidad, el evento sale en su landing propia /o/&lt;slug&gt; con su branding.
            Si no, queda en el catálogo global de pagafacil.eu/entradas.
          </span>
        </label>
      ),
    },
    {
      id: 'publicacion', label: 'Publicación', icon: '🌐',
      content: (
        <div className="space-y-2">
          <Check label="Visible en la web pública (/entradas y /e/…)" checked={form.is_public} onChange={(v) => setForm({ ...form, is_public: v })} />
          <Check label="Promocionar este evento en emails transaccionales" checked={form.promote_in_emails} onChange={(v) => setForm({ ...form, promote_in_emails: v })} />
          <TextArea label="Notas internas (no se muestran al público)" value={form.organizer_note} onChange={(v) => setForm({ ...form, organizer_note: v })} rows={2} />
        </div>
      ),
    },
  ];

  return (
    <form onSubmit={submit}>
      <StickyFormLayout
        title={isEdit ? 'Editar evento' : 'Nuevo evento'}
        subtitle={<>La ficha se muestra en <code>/entradas</code> y <code>/e/{form.slug || 'slug'}</code></>}
        sections={sections}
        footerActions={
          <button type="submit" disabled={busy} className="w-full px-4 py-2.5 bg-brand text-white rounded-md font-semibold disabled:opacity-50">
            {busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
          </button>
        }
      />
    </form>
  );
}

/* ── Pedidos ───────────────────────────────────────────────────── */

function ShowOrders({ token, show }: { token: string; show: Show }) {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get<OrderRow[]>(`/admin/shows/${show.id}/orders?t=${encodeURIComponent(token)}`)
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Error'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [show.id, token]);

  const confirmRedsys = async (o: OrderRow) => {
    const auth = prompt(
      `Confirmar pago Redsys para ${o.order_reference}\n` +
      `Cliente: ${o.buyer_name} (${o.buyer_email})\n` +
      `Importe: ${o.total_amount.toFixed(2)}€\n\n` +
      `1. Ve a canales.redsys.es\n` +
      `2. Busca la operación con este número: ${(o as any).payment_reference || '(sin nº — busca por email del cliente)'}\n` +
      `3. Comprueba que fue APROBADA y anota el auth_code\n\n` +
      `Introduce aquí el auth_code:`
    );
    if (!auth) return;
    try {
      await api.post(
        `/admin/shows/orders/${encodeURIComponent(o.order_reference)}/manual-confirm?t=${encodeURIComponent(token)}`,
        { auth_code: auth.trim(), notes: `Confirmación manual desde admin ${show.slug}` }
      );
      toast.success(`✓ ${o.order_reference} confirmado · email enviado a ${o.buyer_email}`);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al confirmar');
    }
  };

  const totals = useMemo(() => {
    const paid = orders.filter((o) => o.status === 'paid');
    return {
      total_orders: orders.length,
      paid_orders: paid.length,
      tickets_sold: paid.reduce((n, o) => n + o.quantity, 0),
      revenue: paid.reduce((n, o) => n + o.total_amount, 0),
    };
  }, [orders]);

  return (
    <div>
      <div className="mb-4 bg-paper border border-line rounded-lg p-4">
        <div className="font-display font-bold text-lg">{show.title}</div>
        <div className="text-[13px] text-ink-500 mt-1">
          {new Date(show.event_datetime).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
          {show.venue_city ? ` · ${show.venue_city}` : ''}
        </div>
        <div className="mt-3 grid grid-cols-4 gap-4 text-center">
          <Stat label="Pedidos" value={totals.total_orders} />
          <Stat label="Pagados" value={totals.paid_orders} />
          <Stat label="Entradas vendidas" value={totals.tickets_sold} />
          <Stat label="Ingresos" value={`${totals.revenue.toFixed(2)} €`} />
        </div>
      </div>

      {loading && <div className="opacity-60">Cargando pedidos…</div>}

      {!loading && orders.length === 0 && (
        <div className="bg-paper border border-line rounded-lg p-10 text-center text-ink-500">
          Aún no hay pedidos para este evento.
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="bg-paper border border-line rounded-lg overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="text-left text-ink-500 border-b border-line">
              <tr>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Ref</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Comprador</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Cant</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Total</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Estado</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Validadas</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest">Creado</th>
                <th className="px-3 py-2 font-mono uppercase text-[10.5px] tracking-widest text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-line last:border-0">
                  <td className="px-3 py-2 font-mono">{o.order_reference}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{o.buyer_name}</div>
                    <div className="text-[12px] text-ink-500">{o.buyer_email}</div>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{o.quantity}</td>
                  <td className="px-3 py-2 tabular-nums">{o.total_amount.toFixed(2)} €</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10.5px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${o.status === 'paid' ? 'bg-olive-50 text-olive-700' : o.status === 'refunded' ? 'bg-red-100 text-red-700' : 'bg-line text-ink-500'}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums">{o.tickets_validated} / {o.quantity}</td>
                  <td className="px-3 py-2 text-[12px] text-ink-500">
                    {new Date(o.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {o.status === 'pending' && (
                      <button
                        onClick={() => confirmRedsys(o)}
                        title="El cliente dice que le cobraron pero no recibió entrada. Verifica en canales.redsys.es y confirma manualmente aquí."
                        className="text-[11px] font-medium bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded"
                      >
                        🔗 Redsys pagó
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="font-display font-bold text-2xl">{value}</div>
      <div className="text-[10.5px] font-mono uppercase tracking-widest text-ink-500">{label}</div>
    </div>
  );
}

/* ── Inputs ────────────────────────────────────────────────────── */

function Text({
  label,
  value,
  onChange,
  type = 'text',
  step,
  required,
  disabled,
  placeholder,
  className,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  help?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className || ''}`}>
      <span className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500">
        {label}{required && ' *'}
      </span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="border border-line rounded-md px-3 py-2 text-[14px] bg-surface disabled:opacity-60"
      />
      {help && <span className="text-[11px] text-ink-500">{help}</span>}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  rows = 3,
  help,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  help?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500">
        {label}{required && ' *'}
      </span>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="border border-line rounded-md px-3 py-2 text-[14px] bg-surface"
      />
      {help && <span className="text-[11px] text-ink-500">{help}</span>}
    </label>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-[14px]">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
